// assistant-launcher.tsx — Triggers that open the LifeAI Assistant panel.
// Renders TWO triggers sharing one open state + Ctrl/Cmd+J shortcut:
//   • mobile  → compact icon button styled for the app header (lg:hidden)
//   • desktop → floating gbrand pill bottom-right (hidden lg:block)
// Self-hides entirely when !aiAvailable(api). Renders AssistantPanel when open.

/* global KeyboardEvent */

import { lazy, Suspense, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { aiAvailable } from "../lib/ai/ai-config";
import type { LifeOsApi } from "../lib/life-os-contract";

// Lazy: the assistant pulls in the whole AI subsystem (agent loop, tools, markdown,
// file/plan extraction) — load it only when the user opens the chat.
const AssistantPanel = lazy(() => import("./assistant-panel").then((m) => ({ default: m.AssistantPanel })));

interface Props {
  api: LifeOsApi;
}

export function AssistantLauncher({ api }: Props) {
  const [open, setOpen] = useState(false);

  // Global keyboard shortcut: Ctrl/Cmd + J
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key === "j") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Gate: render nothing when AI is not available
  if (!aiAvailable(api)) return null;

  return (
    <>
      {/* MOBILE — header icon button (sits inline in the app header on <lg). */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Mở LifeAI Assistant (Ctrl+J)"
        title="LifeAI Assistant (Ctrl+J)"
        className="gbrand grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white shadow-sm ring-1 ring-white/20 transition active:scale-95 lg:hidden"
      >
        <Sparkles className="h-4 w-4" />
      </button>

      {/* DESKTOP — floating pill bottom-right (no bottom-nav present on lg). */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Mở LifeAI Assistant (Ctrl+J)"
        title="LifeAI Assistant (Ctrl+J)"
        className={[
          "group fixed bottom-6 right-6 z-[60] hidden lg:flex",
          "gbrand h-14 w-auto items-center justify-center gap-2 rounded-2xl px-5 text-white",
          "shadow-[0_10px_30px_rgba(124,58,237,0.4)] ring-1 ring-white/20",
          "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(124,58,237,0.5)] active:scale-95",
        ].join(" ")}
      >
        <Sparkles className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:rotate-12" />
        <span className="text-[14px] font-bold">LifeAI</span>
      </button>

      {open && (
        <Suspense fallback={null}>
          <AssistantPanel api={api} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
